/*
 * Copyright (C) 2009-2025 SonarSource Sàrl
 * All rights reserved
 * mailto:info AT sonarsource DOT com
 */

import {
  Button,
  ButtonGroup,
  ButtonVariety,
  Card,
  LinkStandalone,
  Text,
  TextSize,
} from '@sonarsource/echoes-react';
import { FormattedMessage, useIntl } from 'react-intl';
import DateFormatter from '~shared/components/intl/DateFormatter';
import { JiraOrganizationBinding } from '../../types';
import { OrganizationJiraUnlinkButton } from './OrganizationJiraUnlinkButton';

interface OrganizationJiraInstanceDetailsProps {
  binding: JiraOrganizationBinding;
  isDeletingBinding: boolean;
  isPatchingBinding: boolean;
  onDisconnect: () => void;
  onReauthorize: () => void;
}

export function OrganizationJiraInstanceDetails({
  binding,
  isDeletingBinding,
  isPatchingBinding,
  onReauthorize,
  onDisconnect,
}: Readonly<OrganizationJiraInstanceDetailsProps>) {
  const { formatMessage } = useIntl();

  return (
    <Card>
      <Card.Header
        hasDivider
        rightContent={
          <ButtonGroup>
            <Button
              aria-label={formatMessage({ id: 'organization.jira.binding.connected.reauthorize' })}
              name="reauthorize"
              onClick={onReauthorize}
              type="reset"
              variety={ButtonVariety.Default}
            >
              <FormattedMessage id="organization.jira.binding.connected.reauthorize" />
            </Button>

            <OrganizationJiraUnlinkButton
              isPending={isDeletingBinding || isPatchingBinding}
              onDisconnect={onDisconnect}
            />
          </ButtonGroup>
        }
        title={
          <Text isHighlighted size={TextSize.Large}>
            <FormattedMessage id="organization.jira.binding.instance_details_title" />
          </Text>
        }
      />
      <Card.Body>
        <div className="sw-flex sw-gap-9">
          <div className="sw-flex sw-flex-col sw-gap-3">
            <Text isHighlighted>
              <FormattedMessage id="organization.jira.binding.instance_url.label" />
            </Text>
            <Text isHighlighted>
              <FormattedMessage id="organization.jira.binding.connected_by_on_label" />
            </Text>
          </div>
          <div className="sw-flex sw-flex-col sw-gap-3">
            <Text as="p">
              <LinkStandalone
                className="fs-mask"
                enableOpenInNewTab
                highlight="default"
                to={binding.jiraInstanceUrl}
              >
                {binding.jiraInstanceUrl.replace(/https?:\/\//, '')}
              </LinkStandalone>
            </Text>
            <Text isSubtle>
              <FormattedMessage
                id="organization.jira.binding.connected_by_on_value"
                values={{
                  createdBy: <span className="fs-mask">{binding.createdBy}</span>,
                  createdAt: <DateFormatter date={binding.createdAt} />,
                }}
              />
            </Text>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}
